/**
 * stepKey — how events are correlated into ONE step.
 *
 * Isolated in its own file because getting it wrong silently merges or splits
 * steps, and it is the highest-value thing in the module to test.
 */

import type { DomainInterpreter, NormalizedEvent, StepKey } from './types';

const GENERIC_ID_KEYS = [
  'step_id',
  'stepId',
  'tool_call_id',
  'toolCallId',
  'artifact_id',
  'artifactId',
  'interaction_id',
  'interactionId',
  'skill_id',
  'subagent_id',
  'id'
] as const;

function firstScalar(
  data: Record<string, unknown>,
  keys: readonly string[]
): string | undefined {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
  }
  return undefined;
}

/**
 * Resolve the step key for an event.
 */
export function resolveStepKey(
  event: NormalizedEvent,
  interpreter?: DomainInterpreter
): StepKey {
  const domain = event.domain === '' ? 'unknown' : event.domain;

  const own = interpreter?.identify?.(event);
  if (own) {
    return own.includes(':') ? own : `${domain}:${own}`;
  }

  const generic = firstScalar(event.data, GENERIC_ID_KEYS);
  if (generic) {
    return `${domain}:${generic}`;
  }

  if (event.runId) {
    return `${domain}:${event.runId}`;
  }

  return `${domain}:${event.turnId ?? 'no-turn'}:${event.ordinal}`;
}

/**
 * The key a SETTLED `additional_steps` entry maps to, so a live step and its settled
 * counterpart reconcile onto the same row.
 */
export function settledEntryKey(entry: {
  type?: string;
  id?: string;
  tool_call_id?: string;
  tool_name?: string;
}): SettledEntryIdentity | undefined {
  if (entry.tool_call_id) {
    return { key: `tool:${entry.tool_call_id}`, domain: 'tool' };
  }
  if (isInterruptEntryType(entry.type) && entry.tool_name) {
    return {
      key: `interaction:${entry.tool_name}`,
      domain: 'interaction'
    };
  }
  const runId = stripRunPrefix(entry.id);
  return runId ? { key: `message:${runId}`, domain: 'message' } : undefined;
}

/**
 * A settled entry's identity: the step it folds onto, and the domain that owns
 * it.

 */
export interface SettledEntryIdentity {
  key: StepKey;
  domain: string;
}

/**
 * Does this settled entry describe an agent pause rather than a LangChain message?
 */
export function isInterruptEntryType(type: string | undefined): boolean {
  return type === 'user_confirmation' || type === 'user_auth';
}

/** `"lc_run--019fd0ae-fd72-…"` → `"019fd0ae-fd72-…"`. */
export function stripRunPrefix(id: string | undefined): string | undefined {
  if (!id) {
    return undefined;
  }
  return id.startsWith(LC_RUN_PREFIX) ? id.slice(LC_RUN_PREFIX.length) : id;
}

/** LangChain's prefix on `additional_steps[].id`. */
export const LC_RUN_PREFIX = 'lc_run--';
